import { Form, Formik } from 'formik';
import { useUpdateErpCustomerUserMutation } from 'gcs-common/clients/api/entities/connections/connections-api';
import { MAIL_REGEX, PHONE_OR_MAIL_REGEX } from 'gcs-common/constants/formValidation';
import { getSelectedErpCustomer } from 'gcs-common/slices/administration/erpCustomers/erpCustomersSelectors';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import Button from '../../../Button/Button';
import FormikInput from '../../../FormikInput/FormikInput';
import styles from './styles.module.scss';

// TODO: remove later when migrating to TS/react-query
interface User {
  id: string;
  email: string | undefined;
  phoneNumber: string | undefined;
  friendlyName: string;
}

interface UserUpdateFormValues {
  erpCustomerName: string | undefined;
  customerNumber: string | undefined;
  oldPhoneOrEmail: string | undefined;
  name: string | undefined;
  phoneOrEmail: string;
}

interface ErpCustomerUserUpdateProps {
  user: User;
  erpCustomerId: string;
  onCancel: VoidFunction;
  onSuccess: VoidFunction;
}

const ErpCustomerUserUpdate = (props: ErpCustomerUserUpdateProps) => {
  const { user, erpCustomerId, onCancel, onSuccess } = props;

  const selectedErpCustomer = useSelector(getSelectedErpCustomer(erpCustomerId));
  const { mutateAsync, isPending, isError } = useUpdateErpCustomerUserMutation();
  const [errorMessage, setErrorMessage] = useState('');
  const oldPhoneNumberOrEmail = user.email || user.phoneNumber;

  const onEmailorPhoneChanged = () => {
    setErrorMessage('');
  };

  const UserUpdateValidationSchema = Yup.object().shape({
    phoneOrEmail: Yup.string()
      .nullable()
      .required('Erforderlich')
      .matches(PHONE_OR_MAIL_REGEX, 'Bitte gib eine gültige Mobilnummer oder E-Mail-Adresse ein.')
      .notOneOf([oldPhoneNumberOrEmail], 'Der neue Login darf nicht mit dem alten übereinstimmen'),
  });

  const submit = async (values: UserUpdateFormValues) => {
    const body: Parameters<typeof mutateAsync>[0]['body'] = {
      email: undefined,
      phoneNumber: undefined,
    };

    const phoneOrEmail = values.phoneOrEmail;

    if (MAIL_REGEX.test(phoneOrEmail.trim())) {
      body.email = values.phoneOrEmail;
    } else {
      body.phoneNumber = values.phoneOrEmail.replace(/^(0049|0)/, '+49');
    }

    try {
      await mutateAsync({
        path: {
          connection_id: erpCustomerId,
          craftsman_id: user.id,
        },
        body,
      });
      onSuccess();
    } catch (error) {
      // @ts-ignore error
      setErrorMessage(error.message);
    }
  };

  const initialData: UserUpdateFormValues = {
    erpCustomerName: selectedErpCustomer?.friendlyName,
    customerNumber: selectedErpCustomer?.customerNumber,
    oldPhoneOrEmail: oldPhoneNumberOrEmail,
    name: user.friendlyName,
    phoneOrEmail: '',
  };

  return (
    <Formik<UserUpdateFormValues>
      initialValues={initialData}
      validationSchema={UserUpdateValidationSchema}
      onSubmit={submit}
    >
      {() => (
        <Form className={styles.column}>
          <div className={styles.row}>
            <FormikInput label="Firmename" name="erpCustomerName" disabled />
            <FormikInput label="Kundennummer" name="customerNumber" disabled />
          </div>

          <div className={styles.row}>
            <FormikInput label="Name" type="text" name="name" disabled />
          </div>
          <div className={styles.row}>
            <FormikInput label="Bisherige Mobilnummer oder Email-Adresse" type="text" name="oldPhoneOrEmail" disabled />
          </div>

          <div className={styles.row}>
            <FormikInput
              label="Neue Mobilnummer oder Email-Adresse*"
              type="text"
              name="phoneOrEmail"
              disabled={isPending}
              onKeyUp={onEmailorPhoneChanged}
            />
          </div>

          {isError && errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <div className={styles.buttonRow}>
            <Button className={styles.modalButtonSecondary} type="button" disabled={isPending} onClick={onCancel}>
              Abbrechen
            </Button>
            <Button className={styles.modalButton} type="submit" disabled={isPending}>
              Speichern
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ErpCustomerUserUpdate;
